import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import ChevronIcon from 'components/ChevronIcon/ChevronIcon'
import ActionButton from 'components/ActionButton'
import {
  AccordionContainer,
  ActivityName,
  Answered,
  CollapsibleContent,
  ObjectiveTable,
  ObjectiveTableCell,
  ObjectiveTableHeadCell,
  ObjectiveTableRow,
  ObjectiveCard,
  ObjectiveHeader,
  ObjectiveTitle,
  objectiveTableColumnWidths,
  TableHeader,
  TablePanel,
  TableScrollContainer,
  ObjectiveDividerRow,
  ObjectiveDividerLine,
  ObjectiveDividerCell
} from './styles'
import { mapSummativeRow, SummativePayload } from './assessmentService'
import { DisableBlock } from 'common/common-styles'
import ObjectivesSkeleton from './ObjectivesSkeleton'
import {
  getActivityText,
  getProgressPercentageBars,
  getScoreText,
  getTimeSpentText
} from './shared'
import { AssessmentType } from 'interfaces/app'

type SummativeObjectivesAccordionProps = {
  summatives?: unknown[]
  loading?: boolean
  onStartOrResume: (type: AssessmentType, row: any) => void
  focusedSummativeId?: number | null
  onFocusHandled?: () => void
}

const SummativeObjectivesAccordion = ({
  summatives = [],
  loading = false,
  onStartOrResume,
  focusedSummativeId = null,
  onFocusHandled
}: SummativeObjectivesAccordionProps) => {
  const rows = useMemo(
    () =>
      summatives.map((item, index) =>
        mapSummativeRow(item as SummativePayload, index)
      ),
    [summatives]
  )

  const [isExpanded, setIsExpanded] = useState(true)
  const [highlightedSummativeId, setHighlightedSummativeId] = useState<
    number | null
  >(null)
  const rowRefs = useRef<Record<string, HTMLTableRowElement | null>>({})

  useEffect(() => {
    if (!focusedSummativeId || !rows.length) return

    const targetRow = rows.find((row) => Number(row.id) === focusedSummativeId)
    if (!targetRow) return

    setIsExpanded(true)

    const scrollAndHighlight = () => {
      const rowElement = rowRefs.current[String(targetRow.id)]
      rowElement?.scrollIntoView({ behavior: 'smooth', block: 'center' })

      setHighlightedSummativeId(focusedSummativeId)

      window.setTimeout(() => {
        setHighlightedSummativeId((currentId) =>
          currentId === focusedSummativeId ? null : currentId
        )
      }, 2500)

      onFocusHandled?.()
    }

    window.setTimeout(scrollAndHighlight, 220)
  }, [focusedSummativeId, rows, onFocusHandled])

  if (loading) {
    return <ObjectivesSkeleton type='benchmark' />
  }

  if (!rows.length) return null

  function shouldDisableActionButton(row: any) {
    const isA2Benchmark = row.name?.toLowerCase().includes('- a2')

    if (isA2Benchmark && row.actionLabel === 'View') {
      return false
    }

    return row.disabled
  }

  return (
    <AccordionContainer>
      <ObjectiveCard $expanded={isExpanded} type='benchmark'>
        {!isExpanded && (
          <ObjectiveHeader
            type='button'
            onClick={() => setIsExpanded((value) => !value)}
          >
            <span className='flex'>
              <ChevronIcon direction={isExpanded ? 'up' : 'down'} />
              {/* <ChevronIcon
              src={isExpanded ? chevronUpBlackIcon : chevronDownBlackIcon}
              alt={isExpanded ? 'Collapse benchmarks' : 'Expand benchmarks'}
            /> */}
              <ObjectiveTitle style={{ marginLeft: 13 }}>
                Assessments
              </ObjectiveTitle>
            </span>
          </ObjectiveHeader>
        )}

        <CollapsibleContent $expanded={isExpanded}>
          <TableScrollContainer>
            <TableHeader>
              <ObjectiveTable>
                <colgroup>
                  {objectiveTableColumnWidths.map((width, index) => (
                    <col key={`head-col-${index}`} style={{ width }} />
                  ))}
                </colgroup>
                <thead>
                  <tr>
                    {isExpanded ? (
                      <ObjectiveTableHeadCell
                        style={{ display: 'flex' }}
                        $heading
                        onClick={() => setIsExpanded((value) => !value)}
                      >
                        <ChevronIcon direction={isExpanded ? 'up' : 'down'} />
                        <ObjectiveTitle style={{ marginLeft: 13 }}>
                          Assessments
                        </ObjectiveTitle>
                      </ObjectiveTableHeadCell>
                    ) : (
                      <ObjectiveTableHeadCell />
                    )}
                    <ObjectiveTableHeadCell />
                    <ObjectiveTableHeadCell>
                      Questions Answered
                    </ObjectiveTableHeadCell>
                    <ObjectiveTableHeadCell>Score</ObjectiveTableHeadCell>
                    <ObjectiveTableHeadCell>
                      Time
                      <br />
                      Spent
                    </ObjectiveTableHeadCell>
                    <ObjectiveTableHeadCell>Action</ObjectiveTableHeadCell>
                  </tr>
                </thead>
              </ObjectiveTable>
            </TableHeader>

            <TablePanel>
              <ObjectiveTable>
                <colgroup>
                  {objectiveTableColumnWidths.map((width, index) => (
                    <col key={`body-col-${index}`} style={{ width }} />
                  ))}
                </colgroup>
                <tbody>
                  {rows.map((row, rowIndex) => (
                    <Fragment key={row.id}>
                      <ObjectiveTableRow
                        $disabled={row.disabled}
                        $highlighted={
                          highlightedSummativeId !== null &&
                          Number(row.id) === highlightedSummativeId
                        }
                        ref={(node) => {
                          rowRefs.current[String(row.id)] = node
                        }}
                      >
                        <ObjectiveTableCell $alignStart>
                          <ActivityName $disabled={row.disabled}>
                            {row.name}
                          </ActivityName>
                        </ObjectiveTableCell>

                        <ObjectiveTableCell>
                          {getActivityText(row)}
                        </ObjectiveTableCell>

                        <ObjectiveTableCell>
                          <Answered>{getProgressPercentageBars(row)}</Answered>
                        </ObjectiveTableCell>

                        <ObjectiveTableCell
                          style={{
                            paddingLeft: row?.score?.includes('%') ? 5 : 0
                          }}
                        >
                          {getScoreText(row)}
                        </ObjectiveTableCell>
                        <ObjectiveTableCell>
                          {getTimeSpentText(row)}
                        </ObjectiveTableCell>

                        <ObjectiveTableCell>
                          {shouldDisableActionButton(row) ? (
                            <div className='flex'>
                              <DisableBlock
                                width={88}
                                height={32}
                                radius={999}
                              />
                            </div>
                          ) : (
                            <ActionButton
                              width={84}
                              label={row.actionLabel}
                              onClick={() =>
                                onStartOrResume(AssessmentType.Benchmarks, row)
                              }
                              variant={
                                row.actionLabel === 'View' ? 'info' : 'success'
                              }
                            />
                          )}
                        </ObjectiveTableCell>
                      </ObjectiveTableRow>

                      {rowIndex < rows.length - 1 && (
                        <ObjectiveDividerRow>
                          <ObjectiveDividerCell colSpan={6}>
                            <ObjectiveDividerLine />
                          </ObjectiveDividerCell>
                        </ObjectiveDividerRow>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </ObjectiveTable>
            </TablePanel>
          </TableScrollContainer>
        </CollapsibleContent>
      </ObjectiveCard>
    </AccordionContainer>
  )
}

export default SummativeObjectivesAccordion
